import React, { use, useEffect, useState } from 'react';
import Sidebar from "../../components/Sidebar/Sidebar";
import { Pencil, Plus } from "lucide-react";
import Footer from "../../components/Footer/Footer";
import MedicationsCalendar from '../../components/Medications/MedicationsCalendar';
import MedicationModal from '../../components/Medications/MedicationsModal';
import { createMedication, deleteMedication, getMedications, getTodayMedications, updateMedication } from '../../api/medicationsApi';
import EditMedicationModal from '../../components/Medications/EditMedicationsModal';
import MedicationDetailsModal from '../../components/Medications/MedicationsDetailsModal';

export default function Medications() {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [medications, setMedications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [todayMeds, setTodayMeds] = useState([]);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [modalSource, setModalSource] = useState(null);

  const handleEventClick = (info) => {
    const med = info.event.extendedProps.medication;
    setSelectedMedication(med);
    setModalSource('calendar');
  };

  useEffect(() => {
    fetchTodayMeds();
  }, []);

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const userId = localStorage.getItem("userId")

        if (!userId) {
          return;
        }

        const data = await getMedications(userId);
        setMedications(data);
      } catch (error) {
        console.error("Error fetching medications:", error);
      }
    };
    fetchMedications();
  }, []);

  const fetchTodayMeds = async () => {
    try {
      const userId = localStorage.getItem("userId")

      if (!userId) {
        return;
      }
      const data = await getTodayMedications(userId);
      setTodayMeds(data);
    } catch (error) {
      console.error("Error fetching today's medications:", error);
    }
  };

  const handleAddMedication = async (newMed) => {
    try {
      const userId = localStorage.getItem("userId");

      if (!userId) {
        return;
      }

      const newMedication = await createMedication({ ...newMed, user_id: userId }); // hard coded for now

      const data = await getMedications(userId); // Refresh medications after adding
      setMedications(data);
      fetchTodayMeds(); // Refresh today's medications
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error adding medication:", error);
    }
  };

  // Utility function
  const formatTo12Hour = (timeStr) => {
    const [hour, minute] = timeStr.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const formattedHour = ((h + 11) % 12 + 1);
    return `${formattedHour}:${minute} ${ampm}`;
  };

  const generateCalendarEvents = (medications) => {
    const events = [];

    medications.forEach((med) => {
      const times = Array.isArray(med.times) ? med.times : JSON.parse(med.times || '[]');
      const start = new Date(med.start_date);
      const lastDay = med.end_date
        ? new Date(med.end_date)
        : new Date(Date.now() + 1000 * 60 * 60 * 24 * 30); // 30 days

      for (let d = new Date(start); d <= lastDay; d.setDate(d.getDate() + 1)) {
        times.forEach((time) => {
          const [hour, minute] = time.split(':');
          const eventDate = new Date(d);
          eventDate.setHours(hour, minute, 0, 0);

          events.push({
            id: `${med.id}-${eventDate.toISOString()}`,
            start: eventDate.toISOString(),
            allDay: false,
            extendedProps: { medication: med },
          });
        });
      }
    });

    return events;
  };


  return (
    <div className={`ml-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-16'}`}>
      <Sidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />

      <main className="flex-1 p-6 overflow-auto bg-gray-100">
        <h2 className="mb-6 text-3xl font-bold text-gray-800">Your Medications</h2>
        <h1 className="mb-6 text-sm text-gray-500">Please note: This platform does not provide medical advice. Always consult your physician.</h1>

        {medications.length === 0 ? (
          <p className="text-gray-500 text-sm">No medications found.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {medications.map((med) => (
              <div
                key={med.id}
                onClick={() => setSelectedMedication(med)}
                className="cursor-pointer p-2 border rounded-lg shadow-sm hover:shadow-md transition bg-white"
              >
                <h3 className="text-base font-semibold text-gray-800 truncate">{med.drug_name}</h3>
                <p className="text-xs text-gray-600 font-medium">Dosage: {med.dosage}</p>
              </div>
            ))}
          </div>

        )}

        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 mt-6 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
        >
          Add Medication +
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {/* Today's Log */}
          <div className="order-1 md:order-2">
            <h3 className="mb-5 text-xl font-bold">Today's Log</h3>
            {todayMeds.length === 0 ? (
              <p className="text-gray-500 text-sm">No medications scheduled for today.</p>
            ) : (
              <ul className="space-y-3">
                {todayMeds.map((item, i) => (
                  <li
                    key={i}
                    className="p-3 border rounded bg-white text-sm shadow-sm"
                  >
                    {formatTo12Hour(item.time)} — <strong>{item.drug_name}</strong> ({item.dosage})
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Calendar */}
          <div className="order-2 md:order-1">
            <MedicationsCalendar
              events={generateCalendarEvents(medications)}
              onEventClick={handleEventClick}
            />
          </div>
        </div>


      </main>

      <MedicationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleAddMedication} />
      {editingMedication && (
        <EditMedicationModal
          medication={editingMedication}
          onClose={() => setEditingMedication(null)}
          onSave={async (id, updatedData) => {
            await updateMedication(id, updatedData);
            const userId = localStorage.getItem("userId");
            const refreshed = await getMedications(userId);
            setMedications(refreshed);
            fetchTodayMeds(); // Refresh today's medications after update
            setEditingMedication(null);
          }}
          onDelete={async (id) => {
            await deleteMedication(id);
            const userId = localStorage.getItem("userId");
            const refreshed = await getMedications(userId);
            setMedications(refreshed);
            fetchTodayMeds(); // Refresh today's medications after deletion
            setEditingMedication(null);
          }}
        />
      )}

      {selectedMedication && (
        <MedicationDetailsModal
          medication={selectedMedication}
          onClose={() => {
            setSelectedMedication(null);
            setModalSource(null);
          }}
          formatTo12Hour={formatTo12Hour}
          onEdit={() => {
            setEditingMedication(selectedMedication);
            setSelectedMedication(null);
            setModalSource(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
}
